import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';
import type { BalanceSheet, BalanceSheetAccount } from '@/types';


export function BalanceSheet() {
  const { idToken } = useCognitoAuth();
  const [report, setReport] = useState<BalanceSheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [asOf, setAsOf] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (!idToken) return;
    async function fetchBalanceSheet() {
      try {
        const data = await api.get<BalanceSheet>(`/api/reports/balance-sheet?as_of=${asOf}`, { errorCode: ErrorCode.FETCH_BALANCE_SHEET, token: idToken! });
        setReport(data);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to fetch balance sheet');
      } finally {
        setLoading(false);
      }
    }
    void fetchBalanceSheet();
  }, [asOf, idToken]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(Math.abs(amount));
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6 animate-pulse">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
          <div>
            <div className="h-8 w-32 bg-muted rounded mb-2" />
            <div className="h-4 w-48 bg-muted rounded" />
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="h-9 w-36 bg-muted rounded" />
            <div className="h-8 w-24 bg-muted rounded" />
            <div className="h-8 w-16 bg-muted rounded" />
          </div>
        </div>
        <div className="h-4 w-56 bg-muted rounded" />
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-muted rounded-full" />
            <div className="h-4 w-64 bg-muted rounded" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex flex-col gap-6">
            <div className="bg-card rounded-lg border border-border">
              <div className="p-6 pb-2">
                <div className="h-5 w-20 bg-muted rounded" />
              </div>
              <div className="p-6 pt-0 space-y-3">
                {[1,2,3,4].map(i => (
                  <div key={i} className="flex justify-between">
                    <div className="h-4 w-40 bg-muted rounded" />
                    <div className="h-4 w-24 bg-muted rounded" />
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t border-border">
                  <div className="h-4 w-24 bg-muted rounded" />
                  <div className="h-4 w-24 bg-muted rounded" />
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-6">
            <div className="bg-card rounded-lg border border-border">
              <div className="p-6 pb-2">
                <div className="h-5 w-24 bg-muted rounded" />
              </div>
              <div className="p-6 pt-0 space-y-3">
                {[1,2].map(i => (
                  <div key={i} className="flex justify-between">
                    <div className="h-4 w-40 bg-muted rounded" />
                    <div className="h-4 w-24 bg-muted rounded" />
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t border-border">
                  <div className="h-4 w-28 bg-muted rounded" />
                  <div className="h-4 w-24 bg-muted rounded" />
                </div>
              </div>
            </div>
            <div className="bg-card rounded-lg border border-border">
              <div className="p-6 pb-2">
                <div className="h-5 w-16 bg-muted rounded" />
              </div>
              <div className="p-6 pt-0 space-y-3">
                {[1,2].map(i => (
                  <div key={i} className="flex justify-between">
                    <div className="h-4 w-40 bg-muted rounded" />
                    <div className="h-4 w-24 bg-muted rounded" />
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t border-border">
                  <div className="h-4 w-24 bg-muted rounded" />
                  <div className="h-4 w-24 bg-muted rounded" />
                </div>
              </div>
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex justify-between items-center">
                <div className="h-5 w-40 bg-muted rounded" />
                <div className="h-6 w-28 bg-muted rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <i className="bi bi-exclamation-triangle text-3xl text-danger mb-4"></i>
        <p className="text-muted-foreground">Error: {error}</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">No balance sheet data available.</p>
      </div>
    );
  }

  const totalAssets = Number(report.assets?.total ?? 0);
  const totalLiabilities = Number(report.liabilities?.total ?? 0);
  const totalEquity = Number(report.equity?.total ?? 0);

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Balance Sheet</h1>
          <p className="text-sm text-muted-foreground">Statement of Financial Position</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
          <Button variant="outline" size="sm">Export PDF</Button>
          <Button variant="default" size="sm">Print</Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        As of {new Date(asOf).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      {/* Balance Check — always visible first */}
      <Card className={report.is_balanced ? 'border-primary/30 bg-primary/5' : 'border-destructive/30 bg-destructive/5'}>
        <CardContent className="py-4">
          {report.is_balanced ? (
            <div className="flex items-start gap-3">
              <i className="bi bi-check-circle text-xl text-success flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="font-semibold text-success">Balance sheet is balanced</p>
                <p className="text-sm text-muted-foreground">Assets = Liabilities + Equity</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <i className="bi bi-exclamation-triangle-fill text-xl text-danger flex-shrink-0 mt-0.5"></i>
              <div>
                <p className="font-semibold text-danger">Balance sheet is out of balance</p>
                <p className="text-sm text-muted-foreground">Difference: {formatCurrency(totalAssets - totalLiabilities - totalEquity)}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assets */}
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="bi bi-wallet"></i> Assets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card z-10">
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.assets?.accounts ?? []).map((account: BalanceSheetAccount) => (
                      <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded mr-2">{account.code}</code>
                          {account.name}
                        </td>
                        <td className="py-2 px-3 text-right font-mono tabular-nums">
                          {formatCurrency(Number(account.balance))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold bg-muted/50">
                      <td className="py-2 px-3">Total Assets</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums">{formatCurrency(totalAssets)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Liabilities & Equity */}
        <div className="flex flex-col gap-6">
          {/* Liabilities */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="bi bi-credit-card"></i> Liabilities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card z-10">
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.liabilities?.accounts ?? []).map((account: BalanceSheetAccount) => (
                      <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded mr-2">{account.code}</code>
                          {account.name}
                        </td>
                        <td className="py-2 px-3 text-right font-mono tabular-nums">
                          {formatCurrency(Number(account.balance))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold bg-muted/50">
                      <td className="py-2 px-3">Total Liabilities</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums">{formatCurrency(totalLiabilities)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Equity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <i className="bi bi-piggy-bank"></i> Equity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card z-10">
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.equity?.accounts ?? []).map((account: BalanceSheetAccount) => (
                      <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded mr-2">{account.code}</code>
                          {account.name}
                        </td>
                        <td className="py-2 px-3 text-right font-mono tabular-nums">
                          {formatCurrency(Number(account.balance))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold bg-muted/50">
                      <td className="py-2 px-3">Total Equity</td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums">{formatCurrency(totalEquity)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total Liabilities + Equity</span>
                <span className="text-xl font-mono tabular-nums">{formatCurrency(totalLiabilities + totalEquity)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
